/**
 * PATH: packages/email-hub/src/feature/components/AIEmailCopilot.tsx
 * FILE_ID: EMAILHUB-COMPONENT-AI-EMAIL-COPILOT-001
 * PURPOSE: Conversational co-pilot for the inbox AI tab — the operator
 *          chats about the selected email and ACTIONS things inline:
 *          each instruction turn is dry-run proposed by the AI and the
 *          assistant bubble carries the preview + Create-rule button.
 * WHY: Operator 2026-07-09: "Ask AI should open in the side drawer where
 *      AI analysis is present.. and we can chat and get things actioned
 *      there.. just like co-pilot" — the modal RequestRuleDialog flow,
 *      re-homed as a multi-turn chat under the AI Analysis panel.
 * LANDMINE: the LLM runs ONCE per turn behind propose (dry-run, zero
 *      writes); enforcement stays with the deterministic rule engine.
 *      Nothing is created until the operator clicks Create in a bubble.
 * RELATED:
 *   - RequestRuleDialog.tsx: modal sibling (kept for hosts w/o the tab);
 *     describeProposal/describeRuleTrigger are shared from there.
 *   - store/aiCopilotStore.ts: how the context menu opens + seeds this.
 */
import { useEffect, useRef, useState } from 'react'
import { CheckCircle2, Loader2, Send, Sparkles } from 'lucide-react'
import type {
  RuleRequestProposal,
  RuleRequestProposeResult,
} from '@quotech/ui/kanban'
import { cn } from '../../_internal/lib/utils'
import { useToast } from '../../_internal/components/Toast'
import {
  describeProposal,
  type RuleRequestApiSlice,
} from './RequestRuleDialog'

export interface CopilotTurn {
  id: number
  role: 'user' | 'assistant'
  text: string
  /** Assistant turns carrying an actionable rule proposal. */
  proposal?: RuleRequestProposal
  /** Server-authored preview — required to apply (never mint client-side). */
  previewId?: string
  confirmationToken?: string
  matched?: RuleRequestProposeResult['matched_existing']
  /** Set once this turn's proposal has been applied (or superseded). */
  resolved?: 'applied' | 'superseded'
  error?: boolean
}

/** Pure I→O: propose result → assistant turn fields. Exported for tests. */
export function buildAssistantTurn(
  id: number,
  result: RuleRequestProposeResult,
): CopilotTurn {
  return {
    id,
    role: 'assistant',
    text: describeProposal(result.proposal),
    proposal: result.proposal,
    previewId: result.preview_id,
    confirmationToken: result.confirmation_token,
    matched: result.matched_existing,
  }
}

/** Prefer JSON propose — live BE has /propose/ only (stream is optional). */
export function pickProposeFn(api: RuleRequestApiSlice) {
  return api.proposeRuleRequest ?? api.proposeRuleRequestStream
}

export interface AIEmailCopilotProps {
  api: RuleRequestApiSlice
  orgId: number
  emailId: string
  senderAddress?: string | null
  category?: string | null
  /** Prefill for the input (from the context menu's Ask AI entry). */
  seed?: string
  /** Nonce so a repeated openFor re-seeds even for the same email. */
  seedNonce?: number
  onApplied?: () => void
}

export function AIEmailCopilot({
  api,
  orgId,
  emailId,
  senderAddress,
  category,
  seed,
  seedNonce,
  onApplied,
}: AIEmailCopilotProps) {
  const { toast } = useToast()
  const [turns, setTurns] = useState<CopilotTurn[]>([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState<'proposing' | 'applying' | null>(null)
  const idRef = useRef(0)
  const endRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)

  // Seed the input when the context menu opens the co-pilot. Keyed on
  // the nonce so asking again for the same email still re-seeds.
  useEffect(() => {
    if (seed) {
      setInput(seed)
      inputRef.current?.focus()
    }
  }, [seed, seedNonce])

  // Fresh conversation per email — turns are about ONE email's filing.
  useEffect(() => {
    setTurns([])
    setBusy(null)
  }, [emailId])

  useEffect(() => {
    // jsdom has no scrollIntoView — guard so tests exercise the flow.
    endRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [turns, busy])

  const canChat =
    (typeof api.proposeRuleRequest === 'function' ||
      typeof api.proposeRuleRequestStream === 'function') &&
    typeof api.applyRuleRequest === 'function'

  const send = () => {
    const instruction = input.trim()
    const propose = pickProposeFn(api)
    if (!instruction || busy || !propose) return
    setInput('')
    setBusy('proposing')
    setTurns((t) => [
      // A new instruction supersedes older un-applied proposals.
      ...t.map((turn) =>
        turn.proposal && !turn.resolved
          ? { ...turn, resolved: 'superseded' as const }
          : turn,
      ),
      { id: ++idRef.current, role: 'user', text: instruction },
    ])
    propose({ organisationId: orgId, instruction, emailId })
      .then((r) => {
        setTurns((t) => [...t, buildAssistantTurn(++idRef.current, r)])
      })
      .catch((e: Error) => {
        setTurns((t) => [
          ...t,
          {
            id: ++idRef.current,
            role: 'assistant',
            text:
              e.message ||
              'I could not turn that into a rule — try rephrasing.',
            error: true,
          },
        ])
      })
      .finally(() => setBusy(null))
  }

  const apply = (turn: CopilotTurn) => {
    if (!api.applyRuleRequest || !turn.proposal || busy) return
    setBusy('applying')
    api
      .applyRuleRequest({
        organisationId: orgId,
        proposal: turn.proposal,
      })
      .then(() => {
        setTurns((t) => [
          ...t.map((x) => (x.id === turn.id ? { ...x, resolved: 'applied' as const } : x)),
          {
            id: ++idRef.current,
            role: 'assistant',
            text: 'Rule created — matching emails will file themselves from now on.',
          },
        ])
        toast('Rule created', 'success')
        onApplied?.()
      })
      .catch((e: Error) => {
        setTurns((t) => [
          ...t,
          {
            id: ++idRef.current,
            role: 'assistant',
            text: e.message || 'Could not create the rule.',
            error: true,
          },
        ])
      })
      .finally(() => setBusy(null))
  }

  if (!canChat) return null

  const senderDomain = (senderAddress || '').includes('@')
    ? (senderAddress || '').toLowerCase().split('@').pop()
    : null

  return (
    <div
      data-testid="ai-email-copilot"
      className="mt-3 rounded-lg border border-border bg-background flex flex-col"
    >
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden />
        <span className="text-xs font-medium">Ask AI about this email</span>
        {senderDomain && (
          <span className="ml-auto rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">
            {senderDomain}
          </span>
        )}
      </div>

      <div className="max-h-72 overflow-y-auto px-3 py-2 space-y-2">
        {turns.length === 0 && (
          <p className="text-[11px] text-muted-foreground">
            Describe the outcome — e.g.{' '}
            <button
              type="button"
              className="underline decoration-dotted hover:text-foreground"
              onClick={() =>
                setInput(
                  'SaaS renewals from this sender go to a Subscriptions board › Renewals lane',
                )
              }
            >
              “SaaS renewals → Subscriptions › Renewals”
            </button>
            . I propose the rule; nothing is created until you approve it.
          </p>
        )}
        {turns.map((turn) => (
          <div
            key={turn.id}
            data-testid={`copilot-turn-${turn.role}`}
            className={cn(
              'rounded-md px-2.5 py-1.5 text-xs max-w-[92%]',
              turn.role === 'user'
                ? 'ml-auto bg-primary text-primary-foreground'
                : turn.error
                  ? 'bg-destructive/10 text-destructive'
                  : 'bg-muted/60',
            )}
          >
            <p>{turn.text}</p>
            {turn.matched && turn.matched.count > 0 && !turn.resolved && (
              <p className="mt-1 text-[10px] opacity-70">
                Would have matched {turn.matched.count} of your last{' '}
                {turn.matched.scanned} emails.
              </p>
            )}
            {turn.proposal && turn.resolved !== 'superseded' && (
              <div className="mt-1.5 flex items-center gap-2">
                {turn.resolved === 'applied' ? (
                  <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="h-3 w-3" aria-hidden /> Created
                  </span>
                ) : (
                  <button
                    type="button"
                    data-testid="copilot-apply"
                    disabled={busy != null}
                    onClick={() => apply(turn)}
                    className="rounded bg-primary px-2 py-1 text-[10px] text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    {busy === 'applying' ? 'Creating…' : 'Create rule'}
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
        {busy === 'proposing' && (
          <div className="inline-flex items-center gap-1.5 rounded-md bg-muted/60 px-2.5 py-1.5 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Thinking…
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="border-t border-border p-2 flex items-end gap-1.5">
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              send()
            }
          }}
          rows={2}
          maxLength={1000}
          placeholder="Tell the AI what to do with emails like this…"
          data-testid="copilot-input"
          className="flex-1 resize-none rounded-md border border-border bg-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <button
          type="button"
          onClick={send}
          disabled={busy != null || !input.trim()}
          aria-label="Send"
          data-testid="copilot-send"
          className="rounded-md bg-primary p-2 text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
    </div>
  )
}

export default AIEmailCopilot
